// src/context/RecordingContext.js
import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import useAuthStore from "../store/authStore";
import { API_RECORDING_URL } from "../apis/endpoints";

const RecordingContext = createContext(null);

const CHUNK_TIMESLICE_MS = 30000; 

const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(',').pop());
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const fetchWithAuth = async (url, token, options = {}) => {
    const headers = new Headers(options.headers || {});
    if (token) headers.append("Authorization", `Bearer ${token}`);
    headers.append("Content-Type", "application/json");
    return fetch(url, { ...options, headers });
};

export const RecordingProvider = ({ children }) => {
  const { rawToken, userData } = useAuthStore((state) => ({
    rawToken: state.token, 
    userData: state.userData,
  }));
  const agentId = userData?.user_id;
  const record_audio = userData?.record_audio;
  const record_screen = userData?.record_video;

  // --- State ---
  const [recorderState, setRecorderState] = useState("Idle"); 
  const [uploadQueue, setUploadQueue] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  
  // --- Refs ---
  const chunkCounterRef = useRef(0);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null); 
  const checkIntervalRef = useRef(null);

  // --- Force Reset Function ---
  const forceIdleState = useCallback(() => {
    console.log("Forcefully resetting to Idle...");
    
    if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(t => t.stop());
        mediaStreamRef.current = null;
    }
    mediaRecorderRef.current = null;
    if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    setRecorderState("Idle");
  }, []);

  // --- Data Handling ---
  const handleDataAvailable = useCallback((event) => {
    if (event.data && event.data.size > 0) {
      setUploadQueue((prev) => [...prev, {
        index: chunkCounterRef.current++,
        blob: event.data,
        timestamp: Date.now(),
      }]);
    }
  }, []);

  // --- Start Logic ---
  const startRecording = useCallback(async () => {
    // If already recording, don't restart (Prevents loops)
    if (recorderState === "Recording" || recorderState === "Preparing") return;
    if (!agentId) return;

    setRecorderState("Preparing"); 
    setUploadQueue([]);
    chunkCounterRef.current = 0; 

    try {
      let combinedStream = new MediaStream();

      // SCREEN SHARE (Try this first as it triggers the prompt)
      if (record_screen) {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
        screenStream.getTracks().forEach(track => {
            combinedStream.addTrack(track);
            // Handle user clicking "Stop Sharing" in Chrome UI
            track.onended = () => {
                console.log("User clicked Stop Sharing");
                forceIdleState();
            };
        });
      }

      // AUDIO
      if (record_audio) {
        try {
            const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            audioStream.getTracks().forEach(track => combinedStream.addTrack(track));
        } catch (err) { console.warn("Mic access failed or denied", err); }
      }

      mediaStreamRef.current = combinedStream;

      const options = { mimeType: "video/webm; codecs=vp8,opus" };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) delete options.mimeType;

      const recorder = new MediaRecorder(combinedStream, options);
      recorder.ondataavailable = handleDataAvailable;
      
      // Critical: Ensure we handle unexpected stops
      recorder.onstop = () => {
        // Only force idle if we aren't intentionally restarting or logic dictates
        if (mediaStreamRef.current?.active) {
            console.log("Recorder stopped but stream active?");
        } else {
            forceIdleState();
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start(CHUNK_TIMESLICE_MS); // 30s chunks
      setRecorderState("Recording");

      // Safety Interval
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = setInterval(() => {
          const stream = mediaStreamRef.current;
          if (!stream || !stream.active) {
              forceIdleState();
          }
      }, 2000); 

    } catch (err) {
      console.error("Start Error:", err);
      forceIdleState(); 
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setRecorderState("PermissionDenied");
      }
    }
  }, [agentId, record_audio, record_screen, handleDataAvailable, recorderState, forceIdleState]); 

  const stopRecording = useCallback(() => forceIdleState(), [forceIdleState]);

  // --- Upload Logic (Keep exactly as it was) ---
  const uploadChunk = useCallback(async (chunk) => {
    const base64Data = await blobToBase64(chunk.blob);
    const chunkPayload = {
      type: "chunk",
      videoBase64: base64Data,
      chunk_type: "screen_recording",
      agentId: agentId,
    };
    const response = await fetchWithAuth(API_RECORDING_URL, rawToken, {
        method: "POST",
        body: JSON.stringify(chunkPayload), 
    });
    if (!response.ok) throw new Error(`Upload failed: ${response.status}`);
    return response.json();
  }, [agentId, rawToken]); 

  useEffect(() => {
    if (!agentId) return; 
    const processQueue = async () => {
        if (isUploading || uploadQueue.length === 0) return;
        setIsUploading(true);
        try {
            await uploadChunk(uploadQueue[0]);
            setUploadQueue(prev => prev.slice(1));
        } catch (error) {
            console.error("Upload failed (skipping chunk):", error);
            // Don't stop recorder on upload fail, just skip chunk
            setUploadQueue(prev => prev.slice(1));
        } finally {
            setIsUploading(false);
        }
    };
    processQueue();
  }, [uploadQueue, isUploading, agentId, uploadChunk]); 

  return (
    <RecordingContext.Provider value={{ 
        recorderState, 
        startRecording, 
        stopRecording, 
        record_audio, 
        record_screen 
    }}>
      {children}
    </RecordingContext.Provider>
  );
};

export const useRecordingContext = () => useContext(RecordingContext);